import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError } from 'rxjs';
import { ErrorHandlerService } from '../core/service/error-handler.service';
import { MensagemService } from '../shared/services/mensagem.service';

export const SUPPRESS_ERROR_MESSAGE = new HttpContextToken<boolean>(
  () => false
);

/** Métodos que só leem: um 404 neles é ausência de dado, não operação falha. */
const METODOS_DE_LEITURA = ['GET', 'HEAD', 'OPTIONS'];

/**
 * Mensagem do 404 numa escrita, emitida aqui porque `ErrorHandlerService`
 * silencia qualquer 404 — silêncio que faz sentido na leitura, onde o recurso
 * ausente vira o estado vazio da tela, e nenhum na escrita, onde ele significa
 * que nada foi gravado.
 */
const MENSAGEM_RECURSO_INEXISTENTE =
  'O registro não existe mais. Ele pode ter sido excluído por outro usuário: atualize a tela antes de tentar de novo.';

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerInterceptor implements HttpInterceptor {
  private errorService = inject(ErrorHandlerService);
  private mensagemService = inject(MensagemService);


  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next
      .handle(request)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.errorHandler(error, request)
        )
      );
  }

  private errorHandler(
    error: HttpErrorResponse,
    request: HttpRequest<unknown>
  ): Observable<HttpEvent<unknown>> {
    const suppressMessage = request.context.get(SUPPRESS_ERROR_MESSAGE);

    if (error instanceof HttpErrorResponse) {
      if (
        error.error instanceof Blob &&
        error.error.type === 'application/json'
      ) {
        return new Observable<HttpEvent<unknown>>((observer) => {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorMsg = JSON.parse(reader.result as string);
              this.errorService.handleError(
                new HttpErrorResponse({
                  error: errorMsg,
                  headers: error.headers,
                  status: error.status,
                  statusText: error.statusText,
                  url: error.url ?? '',
                }),
                suppressMessage
              );
            } catch {
              this.errorService.handleError(error, suppressMessage);
            }
            observer.error(error);
          };
          reader.onerror = () => {
            this.errorService.handleError(error, suppressMessage);
            observer.error(error);
          };
          reader.readAsText(error.error);
        });
      } else {
        // Se o erro for um erro HTTP, delegamos o tratamento para o serviço de erros
        this.errorService.handleError(error, suppressMessage);

        if (error.status === 404) {
          return this.recursoInexistente(request, suppressMessage, error);
        }
      }
    }

    // Relança o erro para que outros interceptores ou componentes possam tratá-lo
    throw error;
  }

  /**
   * 404 de LEITURA vira sucesso com corpo vazio, e continua assim: é disso que
   * as telas dependem para cair no estado vazio, e quem precisa distinguir
   * "não encontrado" o faz pelo corpo da resposta.
   *
   * Numa ESCRITA o mesmo tratamento afirmava ao usuário que a operação deu
   * certo justamente quando o servidor respondeu que o recurso nem existe — o
   * ajuste de estoque anunciava sucesso sem nada ter sido gravado, e a
   * exclusão confirmava um produto que continuava na listagem. Aqui o erro
   * sobe, e quem chamou decide o que fazer.
   */
  private recursoInexistente(
    request: HttpRequest<unknown>,
    suppressMessage: boolean,
    error: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    if (ehLeitura(request)) {
      return new Observable<HttpEvent<unknown>>((observer) => {
        observer.next(new HttpResponse({ status: 404, body: {} }));
        observer.complete();
      });
    }

    if (!suppressMessage) {
      this.mensagemService.showError(MENSAGEM_RECURSO_INEXISTENTE);
    }

    throw error;
  }
}

function ehLeitura(request: HttpRequest<unknown>): boolean {
  return METODOS_DE_LEITURA.includes(request.method.toUpperCase());
}
